import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Star as PhStar } from "../../PhosphorIcons";
import { HABIT_ICON_OPTIONS, PHOSPHOR_ICON_CATEGORIES, PH_ICON_MAP } from "../../constants/icons";

// ─── SHARED ICON PICKER (used by both Habit and Category pickers) ─────────────
export function PhosphorIconPicker({ value, color = "#d4789a", onChange, categories, iconMap, onSwitch, openOnMount = false, compact = false }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const btnRef = useRef(null);
  const searchRef = useRef(null);

  const selectedEntry = HABIT_ICON_OPTIONS.find(o => o.key === value);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const pickerH = 360;
      const pickerW = 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow < pickerH ? Math.max(8, rect.top - 4 - pickerH) : rect.bottom + 4;
      const left = Math.min(rect.left, window.innerWidth - pickerW - 8);
      setDropPos({ top, left });
      setSearch("");
      setActiveCat("all");
    }
    setOpen(p => !p);
  };

  useEffect(() => {
    if (openOnMount) handleOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allIcons = categories.flatMap(c => c.icons).filter(k => HABIT_ICON_OPTIONS.some(o => o.key === k));
  const displayIcons = (search.trim()
    ? allIcons.filter(k => k.toLowerCase().includes(search.trim().toLowerCase()))
    : activeCat === "all" ? allIcons : (categories.find(c => c.id === activeCat)?.icons || [])
  ).filter(k => HABIT_ICON_OPTIONS.some(o => o.key === k));

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button ref={btnRef} onClick={handleOpen}
        style={{ background: open ? "var(--c-bg-card)" : "var(--c-bg-card)", border: "1.5px solid color-mix(in srgb, var(--c-primary) 40%, transparent)", borderRadius: 12, padding: compact ? "6px 8px" : "6px 12px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, minWidth: compact ? 44 : 52, minHeight: 46 }}
        title="Pick an icon">
        {selectedEntry
          ? <selectedEntry.C size={28} weight="duotone" color={color} />
          : <PhStar size={28} weight="duotone" color={color} />}
      </button>
      {open && createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 19990 }} />
          <div style={{ position: "fixed", zIndex: 19999, top: dropPos.top, left: dropPos.left, background: "var(--c-bg-card)", border: "1.5px solid color-mix(in srgb, var(--c-primary) 40%, transparent)", borderRadius: 16, padding: 10, boxShadow: "0 8px 32px var(--c-shadow)", width: 300 }}>
            {/* Search */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--c-bg-card)", border: "1px solid color-mix(in srgb, var(--c-primary) 40%, transparent)", borderRadius: 10, padding: "5px 10px", marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: "var(--c-text-soft)" }}>🔍</span>
              <input ref={searchRef} value={search} onChange={e => { setSearch(e.target.value); setActiveCat("all"); }}
                placeholder="Search icons…"
                style={{ border: "none", background: "transparent", outline: "none", fontSize: 12, color: "var(--c-text-mid)", width: "100%" }}
                />
              {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--c-text-soft)", padding: 0 }}>✕</button>}
            </div>
            {/* Category tabs */}
            {!search && (
              <div style={{ display: "flex", gap: 4, overflowX: "auto", paddingBottom: 6, marginBottom: 6, scrollbarWidth: "none" }}>
                <button onClick={() => setActiveCat("all")}
                  style={{ flexShrink: 0, fontSize: 10, padding: "3px 8px", borderRadius: 20, border: "none", background: "transparent", color: activeCat === "all" ? "var(--c-text-accent)" : "var(--c-text-soft)", cursor: "pointer", fontWeight: activeCat === "all" ? 700 : 400, textDecoration: activeCat === "all" ? "underline" : "none", textDecorationThickness: 2, textUnderlineOffset: 4 }}>
                  All
                </button>
                {categories.map(c => (
                  <button key={c.id} onClick={() => setActiveCat(c.id)}
                    style={{ flexShrink: 0, fontSize: 10, padding: "3px 8px", borderRadius: 20, border: "none", background: "transparent", color: activeCat === c.id ? "var(--c-text-accent)" : "var(--c-text-soft)", cursor: "pointer", fontWeight: activeCat === c.id ? 700 : 400, textDecoration: activeCat === c.id ? "underline" : "none", textDecorationThickness: 2, textUnderlineOffset: 4 }}>
                    {c.label}
                  </button>
                ))}
              </div>
            )}
            {/* Icon grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 3, maxHeight: 224, overflowY: "auto" }}>
              {displayIcons.map(key => {
                const entry = HABIT_ICON_OPTIONS.find(o => o.key === key);
                if (!entry) return null;
                const { C: Icon } = entry;
                return (
                  <button key={key} onClick={() => { onChange(key); setOpen(false); }} title={key}
                    style={{ background: value === key ? "var(--c-bg-card)" : "transparent", border: value === key ? "1.5px solid var(--c-primary)" : "1.5px solid transparent", borderRadius: 8, padding: "5px 3px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 34 }}>
                    <Icon size={20} weight="duotone" color={value === key ? "var(--c-primary)" : color} />
                  </button>
                );
              })}
              {displayIcons.length === 0 && <p style={{ gridColumn: "1/-1", textAlign: "center", color: "var(--c-text-soft)", fontSize: 12, padding: "12px 0" }}>No icons found</p>}
            </div>
            {onSwitch && <button type="button" onClick={() => { setOpen(false); onSwitch(); }}
              style={{ display: "block", margin: "8px auto 0", padding: "2px 4px", background: "none", border: "none", cursor: "pointer", color: "var(--c-text-accent)", fontFamily: "inherit", fontSize: 13, fontWeight: 800, textDecoration: "underline" }}>
              use emoji
            </button>}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// Habit icon picker — uses Phosphor categories
export function HabitIconPicker({ value, color = "#d4789a", onChange, onSwitch, openOnMount, compact }) {
  return <PhosphorIconPicker value={value} color={color} onChange={onChange} onSwitch={onSwitch} openOnMount={openOnMount} compact={compact} categories={PHOSPHOR_ICON_CATEGORIES} iconMap={PH_ICON_MAP} />;
}

export function LucideIconPicker({ value, color = "#d4789a", onChange, onSwitch, openOnMount }) {
  return <PhosphorIconPicker value={value} color={color} onChange={onChange} onSwitch={onSwitch} openOnMount={openOnMount} categories={PHOSPHOR_ICON_CATEGORIES} iconMap={PH_ICON_MAP} />;
}
